// Client-side Analytics Hook for Real-time Canvas Tracking
import { useEffect } from 'react';

// Generate unique session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

class AnalyticsClient {
  private sessionId: string;
  private sessionStartTime: number;
  private lastInteractionTime: number;
  private isSessionActive: boolean = false;

  constructor() {
    this.sessionId = generateSessionId();
    this.sessionStartTime = Date.now();
    this.lastInteractionTime = Date.now();
  }

  async trackEvent(event: string, data: any = {}): Promise<void> {
    try {
      const analyticsData = {
        sessionId: this.sessionId,
        event,
        timestamp: new Date().toISOString(),
        data: {
          ...data,
          sessionTime: this.getSessionTime(),
          timeSinceLastInteraction: this.getTimeSinceLastInteraction()
        }
      };
      
      // Store in localStorage as backup
      const existingData = JSON.parse(localStorage.getItem('analytics_events') || '[]');
      existingData.push(analyticsData);
      // Keep only last 100 events to prevent localStorage overflow
      if (existingData.length > 100) {
        existingData.splice(0, existingData.length - 100);
      }
      localStorage.setItem('analytics_events', JSON.stringify(existingData));
      
      console.log('📊 Analytics Event Tracked:', {
        sessionId: this.sessionId.substring(0, 20) + '...',
        event,
        timestamp: analyticsData.timestamp,
        data: data
      });
      
      // Send to our actual analytics API
      try {
        const apiUrl = '/api/analytics/store';
        console.log(`🌐 Sending ${event} to API: ${apiUrl}`);
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            sessionId: this.sessionId,
            event,
            data: analyticsData.data
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        console.log(`🌐 API Response status: ${response.status} for ${event}`);
        
        if (!response.ok) {
          console.error(`❌ API Error: Status ${response.status} ${response.statusText}`);
          
          let errorDetails = 'Unknown error';
          try {
            // Try to get response as text first (more reliable)
            const responseText = await response.text();
            console.error(`❌ DETAILED API ERROR RESPONSE:`, responseText);
            
            // Try to parse as JSON if it looks like JSON
            if (responseText.startsWith('{') || responseText.startsWith('[')) {
              try {
                const errorJson = JSON.parse(responseText);
                console.error(`❌ PARSED ERROR DETAILS:`, errorJson);
                
                // Show the enhanced debug information
                if (errorJson.error && errorJson.message) {
                  console.error(`❌ API ERROR: ${errorJson.error}`);
                  console.error(`❌ MESSAGE: ${errorJson.message}`);
                  if (errorJson.step) console.error(`❌ FAILED AT STEP: ${errorJson.step}`);
                  if (errorJson.redisStatus) console.error(`❌ REDIS STATUS: ${errorJson.redisStatus}`);
                  if (errorJson.stack) console.error(`❌ STACK TRACE:`, errorJson.stack);
                }
                
                errorDetails = errorJson;
              } catch {
                errorDetails = responseText;
              }
            } else {
              errorDetails = responseText;
            }
          } catch (readError) {
            console.error(`❌ Failed to read error response:`, readError);
            errorDetails = `Failed to read response: ${readError.message}`;
          }
          
          throw new Error(`Analytics API error: ${response.status} - ${JSON.stringify(errorDetails)}`);
        }
        
        const result = await response.json();
        console.log('✅ Analytics sent successfully:', result);
      } catch (apiError) {
        console.error('❌ Failed to send analytics to API:', apiError);
        console.error('API Error details:', {
          message: apiError.message,
          stack: apiError.stack,
          event: event,
          data: data
        });
        // Continue execution even if analytics fails
      }
      
      this.lastInteractionTime = Date.now();
    } catch (error) {
      console.warn('Analytics error:', error);
    }
  }

  // Session Management
  async startSession(): Promise<void> {
    console.log('🚀 startSession called, isSessionActive:', this.isSessionActive);
    if (this.isSessionActive) return;
    
    this.isSessionActive = true;
    console.log('🚀 Tracking session_start event...');
    await this.trackEvent('session_start');
    
    // Track page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Track session end on page unload
    window.addEventListener('beforeunload', this.endSession.bind(this));
    console.log('🚀 Session started successfully');
  }

  async endSession(): Promise<void> {
    if (!this.isSessionActive) return;
    
    this.isSessionActive = false;
    await this.trackEvent('session_end');
  }

  // Profile Page Tracking
  async trackProfileView(): Promise<void> {
    console.log('👁️ trackProfileView called');
    await this.trackEvent('profile_view');
  }

  async trackCanvasOpen(triggerCard: string, cardType: string, initialText?: string): Promise<void> {
    await this.trackEvent('canvas_open', {
      triggerCard,
      cardType,
      initialText
    });
  }

  // Canvas Interaction Tracking (SUPREME VALUE)
  async trackNoteCreate(noteId: string, position: { x: number; y: number }, userText: string): Promise<void> {
    await this.trackEvent('canvas_interaction', {
      id: `note_${noteId}`,
      type: 'note_create',
      position,
      userText,
      noteId
    });
  }

  async trackNoteEdit(noteId: string, position: { x: number; y: number }, newText: string): Promise<void> {
    await this.trackEvent('canvas_interaction', {
      id: `edit_${noteId}_${Date.now()}`,
      type: 'note_edit',
      position,
      userText: newText,
      noteId
    });
  }

  async trackCanvasPan(fromPosition: { x: number; y: number }, toPosition: { x: number; y: number }): Promise<void> {
    await this.trackEvent('canvas_interaction', {
      id: `pan_${Date.now()}`,
      type: 'canvas_pan',
      position: toPosition,
      data: {
        fromPosition,
        toPosition,
        distance: Math.sqrt(Math.pow(toPosition.x - fromPosition.x, 2) + Math.pow(toPosition.y - fromPosition.y, 2))
      }
    });
  }

  // AI Interaction Tracking (COMPLETE LOGGING)
  async trackAICall({
    noteId,
    conversationId,
    position,
    character,
    userText,
    profileContext,
    conversationContext,
    isLimitReached,
    fullPrompt,
    aiResponse,
    humanName,
    visitorName,
    interactionCount,
    responseTime,
    success,
    error,
    triggerCard
  }: {
    noteId: string;
    conversationId: string;
    position: { x: number; y: number };
    character: 'human' | 'universe';
    userText: string;
    profileContext?: any;
    conversationContext: string[];
    isLimitReached: boolean;
    fullPrompt: string;
    aiResponse: string;
    humanName: string;
    visitorName: string;
    interactionCount: number;
    responseTime: number;
    success: boolean;
    error?: string;
    triggerCard?: string;
  }): Promise<void> {
    // Track the AI call with complete details
    await this.trackEvent('ai_call', {
      noteId,
      conversationId,
      position,
      character,
      userText,
      profileContext,
      conversationContext,
      isLimitReached,
      fullPrompt, // COMPLETE PROMPT SENT TO AI
      aiResponse,
      humanName,
      visitorName,
      interactionCount,
      responseTime,
      success,
      error,
      triggerCard
    });

    // Also track as canvas interaction
    await this.trackEvent('canvas_interaction', {
      id: `ai_${noteId}_${Date.now()}`,
      type: 'ai_interaction',
      position,
      userText,
      aiResponse,
      conversationId,
      noteId
    });
  }

  async trackNoteContinue(parentNoteId: string, newNoteId: string, position: { x: number; y: number }): Promise<void> {
    await this.trackEvent('canvas_interaction', {
      id: `continue_${newNoteId}`,
      type: 'note_continue',
      position,
      parentNoteId,
      noteId: newNoteId
    });
  }

  // Utility Methods
  private getSessionTime(): number {
    return Math.floor((Date.now() - this.sessionStartTime) / 1000);
  }

  private getTimeSinceLastInteraction(): number {
    return Math.floor((Date.now() - this.lastInteractionTime) / 1000);
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      // Page is hidden - user might be leaving
      // Don't end session immediately, just note the time
    } else {
      // Page is visible again - user is back
      this.lastInteractionTime = Date.now();
    }
  }

  // Getters
  getSessionId(): string {
    return this.sessionId;
  }
}

// Global analytics instance
let analyticsInstance: AnalyticsClient | null = null;

// React Hook for Analytics
export const useAnalytics = () => {
  // Always use the global instance if it exists
  if (!analyticsInstance) {
    console.log('🔧 Creating global AnalyticsClient instance');
    analyticsInstance = new AnalyticsClient();
    analyticsInstance.startSession();
  }

  useEffect(() => {
    console.log('🔧 Analytics hook mounted, using global instance');
    
    // Don't end session on component unmount - keep it alive
    return () => {
      console.log('🔧 Analytics hook unmounting (session stays active)');
    };
  }, []);

  console.log('🔧 Analytics hook returning instance:', !!analyticsInstance);
  return analyticsInstance;
};

// Export for direct use
export const getAnalytics = (): AnalyticsClient => {
  if (!analyticsInstance) {
    analyticsInstance = new AnalyticsClient();
    analyticsInstance.startSession();
  }
  return analyticsInstance;
};